import { WebAppUploadStatusItemDto } from '@mi-front/api-v2';

const COMPLETED = 'COMPLETED';

const DISPLAY_STATUSES = [COMPLETED, 'SCHEDULED', 'FAILED'] as const;

const PUBLISHED_PLATFORM_DISPLAY_STATUSES = [COMPLETED, 'FAILED'] as const;

export type DisplayPlatformItem = { id: string; platform: string; status: string };

/**
 * Keeps one row per platform. When a platform has multiple uploads, prefers
 * COMPLETED so the card shows a successful publish even if another upload failed.
 */
function dedupeByPlatform(items: DisplayPlatformItem[]): DisplayPlatformItem[] {
  const byPlatform = new Map<string, DisplayPlatformItem>();
  for (const item of items) {
    const key = item.platform.toUpperCase();
    const existing = byPlatform.get(key);
    if (!existing) {
      byPlatform.set(key, item);
      continue;
    }
    if (item.status === COMPLETED && existing.status !== COMPLETED) {
      byPlatform.set(key, item);
    }
  }
  return Array.from(byPlatform.values());
}

/**
 * Builds platform status rows for social pills from list payloads: prefers
 * `publishedPlatforms` when present, otherwise filters `uploadStatuses` to displayable states.
 * Multiple uploads for the same platform collapse to a single icon.
 */
export function getDisplayPlatformItems(props: {
  uploadStatuses: WebAppUploadStatusItemDto[];
  publishedPlatforms?: ReadonlyArray<{
    platform: string;
    status: string;
    uploadId: string;
  }>;
}): DisplayPlatformItem[] {
  const { uploadStatuses, publishedPlatforms } = props;
  if (publishedPlatforms?.length) {
    return dedupeByPlatform(
      publishedPlatforms
        .filter((p) =>
          PUBLISHED_PLATFORM_DISPLAY_STATUSES.includes(
            p.status as (typeof PUBLISHED_PLATFORM_DISPLAY_STATUSES)[number],
          ),
        )
        .map((p) => ({ id: p.uploadId, platform: p.platform, status: p.status })),
    );
  }
  return dedupeByPlatform(
    uploadStatuses
      .filter((u) => DISPLAY_STATUSES.includes(u.status as (typeof DISPLAY_STATUSES)[number]))
      .map((u) => ({ id: u.id, platform: u.platform, status: u.status })),
  );
}
